import { useEffect } from "react";
import { useSearchParams } from "react-router";

import logoImg from "~/assets/logo.png";
import { Button } from "~/components/ui/button";
import { ONELINK_MARKETING_BASE } from "~/constants";
import { trackLpCtaClick, trackLpView } from "~/lib/track-events";
import { CTA_LABEL, DEFAULT_DRAMA_ID, type LpDrama, lpDramas } from "~/pages/lp/app-single-content";

const PAGE = "app_lp_single";

// `Object.hasOwn` guard, not a bare `lpDramas[id]`: a crafted `?drama=__proto__` / `toString` would
// otherwise resolve an inherited member and slip past the default, crashing the page. Every drama
// (the default included) is guaranteed a non-empty variants array by app-single-content.test.ts.
function resolveDrama(id: string | null): LpDrama {
  return id && Object.hasOwn(lpDramas, id) ? lpDramas[id] : lpDramas[DEFAULT_DRAMA_ID];
}

export default function LpAppSinglePage() {
  const [searchParams] = useSearchParams();

  // Which drama + hook variant to show is chosen by the URL, so each variant is its own ad URL and
  // the ad platform runs the A/B. Unknown values fall back to the default drama / first variant.
  const drama = resolveDrama(searchParams.get("drama"));
  const variantId = searchParams.get("v");
  const variant = drama.variants.find(v => v.id === variantId) ?? drama.variants[0];
  const variantKey = `${PAGE}:${drama.dramaId}:${variant.id}`;

  useEffect(() => {
    trackLpView(variantKey);
  }, [variantKey]);

  function handleDownload(ctaId: string) {
    trackLpCtaClick(variantKey, ctaId);
    // OneLink: `c` = variant tag (installs split by variant, matching the web funnel);
    // `deep_link_value=watch` lands the install on the Watch tab; `pid` (the ad-network id) is
    // forwarded verbatim when present — the same raw param the web event records, so the two join.
    const params = new URLSearchParams({ c: variantKey, deep_link_value: "watch" });
    const pid = searchParams.get("pid");
    if (pid) params.set("pid", pid);
    window.location.href = `${ONELINK_MARKETING_BASE}?${params.toString()}`;
  }

  return (
    // Mobile-first: a phone-width column centered on a dark ground so desktop isn't a full-bleed 1440.
    <div className="bg-base-black flex min-h-dvh w-full justify-center">
      <div className="relative flex min-h-dvh w-full max-w-md flex-col overflow-hidden">
        {/* Blurred fill so the area below the 2:3 poster is its own soft extension, not a black band. */}
        <img
          src={drama.poster}
          alt=""
          aria-hidden
          className="absolute inset-0 z-0 size-full scale-110 object-cover blur-2xl"
        />

        {/* Poster pinned to the top at its natural 2:3; copy pinned to the bottom above it (z-10). */}
        {drama.videoUrl ? (
          <video
            className="absolute inset-x-0 top-0 z-0 w-full"
            src={drama.videoUrl}
            poster={drama.poster}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img className="absolute inset-x-0 top-0 z-0 w-full" src={drama.poster} alt="" />
        )}

        <img src={logoImg} alt="Blidz" className="absolute top-5 left-5 z-10 size-8 rounded-lg" />

        <div className="from-base-black via-base-black/80 relative z-10 mt-auto bg-gradient-to-t to-transparent p-6 pt-16 text-white">
          {(drama.rating != null || drama.viewCount != null) && (
            <div className="mb-2 flex items-center gap-2 text-sm text-white/80">
              {drama.rating != null && <span>★ {drama.rating}</span>}
              {drama.viewCount != null && <span>· {drama.viewCount.toLocaleString()} watching</span>}
            </div>
          )}
          <h1 className="text-2xl leading-snug font-bold sm:text-3xl">{variant.hook}</h1>
          <Button
            id="lp-app-single-download-cta"
            variant="primary"
            size="large"
            className="mt-5 flex w-full"
            onClick={() => handleDownload("lp-app-single-download-cta")}
          >
            {CTA_LABEL}
          </Button>
        </div>
      </div>
    </div>
  );
}
